import { useEffect, useId, useState } from 'react';
import type { Project } from '@mockd/shared';
import { useProjects, useTeams } from '../../hooks';
import { Modal } from '../common/Modal';

interface TransferProjectModalProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
  onTransferred: (project: Project) => void;
}

const PERSONAL = 'personal';

// Move a project between personal and team scope. The target dropdown lists
// Personal plus every team the caller belongs to, excluding the project's
// current scope, and a confirmation line spells out who gains or loses access.
export function TransferProjectModal({ project, isOpen, onClose, onTransferred }: TransferProjectModalProps) {
  const selectId = useId();
  const { teams, fetchTeams } = useTeams();
  const { transferProject } = useProjects();
  const [target, setTarget] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTeams();
      setTarget('');
    }
  }, [isOpen, fetchTeams]);

  // Targets: Personal (unless already personal) + every team except the current one.
  const options = [
    ...(project.teamId ? [{ value: PERSONAL, label: 'Personal (only you)' }] : []),
    ...teams
      .filter((t) => t.id !== project.teamId)
      .map((t) => ({ value: t.id, label: t.name })),
  ];

  const selectedTeam = teams.find((t) => t.id === target);
  let description: string | null = null;
  if (target === PERSONAL) {
    description = 'Only you will keep access. The team will lose access to this project.';
  } else if (selectedTeam) {
    description = `Everyone in ${selectedTeam.name} will get access per their role. It will appear in the team's projects.`;
  }

  const handleTransfer = async () => {
    if (!target) return;
    setIsTransferring(true);
    try {
      const updated = await transferProject(project.id, target);
      onTransferred(updated);
      onClose();
    } catch {
      // Error toast is surfaced by the hook.
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transfer Project" titleClassName="font-bold text-lg mb-4">
      <label htmlFor={selectId} className="text-sm text-base-content/80 block mb-1">Move to</label>
      <select
        id={selectId}
        className="select w-full"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
      >
        <option value="" disabled>
          Select a destination…
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {description && <p className="text-sm text-base-content/70 mt-3">{description}</p>}

      <div className="modal-action">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isTransferring}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleTransfer}
          disabled={!target || isTransferring}
        >
          {isTransferring ? 'Transferring…' : 'Transfer'}
        </button>
      </div>
    </Modal>
  );
}
